import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PaymentService, {
  addVat,
  addVatByRoom,
  formatPrice,
} from '../../Core/Services/PaymentService';
import BookingSummary from '../ReusableComponents/BookingSummary';
import BookingService from '../../Core/Services/BookingService';
import SettingsService from '../../Core/Services/SettingsService';
import DateService from '../../Core/Services/DateService';
import Currencies from '../../Core/Enums/Currencies';
import { loadStripe } from '@stripe/stripe-js';

//services and global state
import { useGlobal } from '../../Core/GlobalState/Store';
import FormLayout from '../ReusableComponents/FormLayout';
import { useStep } from '../Base/StepContext';
import UserIcon from '../Icons/UserIcon';
import CalendarIcon from '../Icons/CalendarIcon';
import PaymentIcon from '../Icons/PaymentIcon';
import ConfirmationSpinner from '../Icons/ConfirmationSpinner';

// Constants
const DATE_LOCALE = 'da-DK';

// Styling constants
const STYLES = {
  borderSolid: '1px solid gray',
  marginTop16: { marginTop: '16px' },
  marginBottom16: { marginBottom: '16px' },
  margin16: { marginTop: '16px', marginBottom: '16px' },
  primaryColor: '#233F72',
  fontFamily: 'Roboto',
};

const createDateFormatter = () =>
  new Intl.DateTimeFormat(DATE_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const stripePromise = loadStripe(SettingsService.getStripePublicKey());

const Confirmation = () => {
  // local state
  const [loadingText, setLoadingText] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const bookingFlowInProgressRef = useRef(false);
  const abortControllerRef = useRef(null);

  const { handleNext } = useStep();

  const { t, i18n } = useTranslation();
  const {
    selectedSize,
    user,
    setUser,
    date,
    room,
    currentLocation,
    titanEmail,
  } = useGlobal();

  const dateFrom = date;
  const formatter = createDateFormatter();
  const firstMonthPrice = addVat(
    PaymentService.calculateProratedAmount(room, dateFrom)
  );

  // Stop the indefinite verification polling if the user leaves this step
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const triggerBookingFlow = async () => {
    if (loading || bookingFlowInProgressRef.current) {
      return;
    }

    bookingFlowInProgressRef.current = true;

    try {
      setLoading(true);
      setErrorText('');

      // Initialize booking service with payment service
      BookingService.setPaymentService(PaymentService);

      // 1. Create the Stripe customer used for verification and billing
      const customer = await BookingService.createStripeCustomer(user);
      const verifiedUser = {
        ...user,
        stripeCustomerId: customer.stripeUserId,
      };
      setUser(verifiedUser);

      // 2. Run identity verification unless the user is already verified
      if (!user.isIdentificationVerified) {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const verificationResult = await BookingService.handleVerificationFlow(
          verifiedUser,
          room,
          dateFrom,
          titanEmail,
          customer.stripeUserId,
          stripePromise,
          currentLocation,
          statusKey => setLoadingText(t(statusKey)),
          controller.signal
        );

        abortControllerRef.current = null;

        if (!verificationResult.success) {
          // User left the step - the component is unmounting, skip state updates
          if (verificationResult.cancelled) {
            return;
          }

          setLoadingText('');
          if (verificationResult.closed) {
            setErrorText(t('verification_closed_retry'));
          } else if (verificationResult.requiredInput) {
            setErrorText(t('error_additional_user_input_required'));
          } else {
            setErrorText(t('verification_failed'));
          }
          setLoading(false);
          return;
        }
      }

      // 3. Verification passed - the debtor, order and payment session are
      // created on the checkout step so we navigate there straight away.
      handleNext();
    } catch (error) {
      console.error('Error during booking flow:', error);
      const message = error?.message;
      if (message === 'error_creating_stripe_customer') {
        setErrorText(t(message));
      } else {
        setErrorText(
          t('fatal_error_occurred_please_try_again_or_contact_support')
        );
      }
      setLoading(false);
    } finally {
      bookingFlowInProgressRef.current = false;
    }
  };

  return (
    <>
      <FormLayout
        title={t('rental_contract_title')}
        loadingText={loadingText}
        loading={loading}
      >
        <div className="md:grid grid-cols-1 flex flex-col-reverse gap-6 md:grid-cols-12 !border !border-solid border-gray-400">
          <div className="col-span-12 md:col-span-7 md:min-h-[800px] m-6 md:m-12 ">
            <h2 className="font-bold text-4xl">
              {t('rental_contract_heading')}
            </h2>
            <div>
              <div style={STYLES.margin16}>
                {t('rental_contract_subheading')}
              </div>
            </div>
            <div className="subgrid !mt-8 !border !border-solid border-gray-400 ">
              <div
                className="p-4 md:p-6"
                style={{ borderBottom: STYLES.borderSolid }}
              >
                <div className="grid grid-cols-12">
                  <div className="font-bold col-span-1">
                    <UserIcon color={STYLES.primaryColor} />
                  </div>
                  <div className="flex flex-col gap-4 col-span-11">
                    <div className="font-bold">
                      {t('rental_contract_your_info')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>{user?.firstName + ' ' + user?.lastName}</div>
                      <div>{user?.address?.streetAddress}</div>
                      <div>{user?.phone}</div>
                      <div>
                        {user?.address?.zipCode} , {user?.address?.city}
                      </div>
                      <div
                        className="text-wrap break-word"
                        style={{ wordBreak: 'break-word' }}
                      >
                        {user?.email}
                      </div>
                      <div>{user?.address?.country}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="p-4 md:p-6"
                style={{ borderBottom: STYLES.borderSolid }}
              >
                <div className="grid grid-cols-12">
                  <div className="font-bold col-span-1">
                    <CalendarIcon color={STYLES.primaryColor} />
                  </div>
                  <div className="col-span-11 flex flex-col gap-4">
                    <div className="font-bold">
                      {t('rental_contract_rental_period')}
                    </div>
                    <div className="flex flex-col gap-4">
                      <div>
                        {t('rental_contract_start_date')}:{' '}
                        <b>{formatter.format(date)}</b>
                      </div>
                      <div>
                        {t('rental_contract_period_until_terminated')}{' '}
                        <div>{t('rental_contract_notice_period')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="p-4 md:p-6"
                style={{ borderBottom: STYLES.borderSolid }}
              >
                <div className="grid grid-cols-12">
                  <div className="font-bold col-span-1">
                    <PaymentIcon color={STYLES.primaryColor} />
                  </div>
                  <div className="col-span-11 flex flex-col gap-4">
                    <div className="font-bold">
                      {t('rental_contract_first_payment')}
                    </div>
                    <div className="flex flex-col gap-4">
                      <div className="flex justify-between">
                        <div>
                          {t(
                            SettingsService.getRateTranslationKey(
                              'rental_contract_first_month_rent'
                            )
                          )}{' '}
                          (
                          {PaymentService.calculateProratedAmountInDays(date)}{' '}
                          {t('rental_contract_of')}
                          {new Date(
                            dateFrom.getFullYear(),
                            dateFrom.getMonth() + 1,
                            0
                          ).getDate()}{' '}
                          {t('rental_contract_days')})
                        </div>
                        <div className="font-bold">
                          {formatPrice(
                            addVat(
                              PaymentService.calculateProratedAmountOnly(
                                room,
                                date
                              )
                            ),
                            i18n.language
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <div>{t('rental_contract_prepaid_rent')}</div>
                        <div className="font-bold">
                          {formatPrice(addVatByRoom(room), i18n.language)}
                        </div>
                      </div>
                    </div>
                    <hr />
                    <div
                      className="flex justify-between"
                      style={{ ...STYLES.marginTop16 }}
                    >
                      <div className="font-bold">
                        {t('rental_contract_total')}
                      </div>
                      <div className="font-bold text-green-600">
                        {formatPrice(
                          addVat(
                            PaymentService.calculateProratedAmount(room, date)
                          ),
                          i18n.language
                        )}{' '}
                        {Currencies.getCodeByValue(
                          room.SelfStoreMonthPriceUniCurrency
                        )}
                        . {t('incl_vat')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2" style={{ ...STYLES.margin16 }}></div>
            {errorText && (
              <div className="text-red-600 text-sm mt-2 mb-2">{errorText}</div>
            )}
            <button
              type="button"
              disabled={loading}
              onClick={triggerBookingFlow}
              className="text-white bg-primaryblue hover:bg-blue-700 disabled:hover:bg-primaryblue disabled:opacity-60 disabled:cursor-not-allowed py-4 w-full text-center cursor-pointer mt-4"
              style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              {loading ? <ConfirmationSpinner /> : t('confirm_booking')}
            </button>
          </div>

          <div className="col-span-12 md:col-span-5 ">
            <BookingSummary firstMonthPrice={firstMonthPrice} showAutoRenew />
          </div>
        </div>
      </FormLayout>
    </>
  );
};

export default Confirmation;
